import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FilterIcon } from "lucide-react";
import Button from "@components/ButtonComponent";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import BadgeComponent from "@components/BadgeComponent";
import { getMixedFontClass } from "@/utils/font-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon, X } from "lucide-react";

const FilterSection = ({
  filtersLoading,
  filterState,
  cumulativeFromDate,
  tempCumulativeFromDate,
  selectedBusinessDay,
  setCumulativeFromDate,
  setDebouncedCumulativeDate,
  setTempCumulativeFromDate,
  availableFilters,
  filtersData,
  topicsData,
  handleFilterChange,
  removeFilter,
  setFilterState,
  hasUnappliedChanges,
  handleApplyFilters,
  loading,
}) => {
  const resolveAppliesToTopic = (filterTag) => {
    const appliesToTopics = topicsData.Topics.filter((topic) =>
      topic.AvailableFilterTags.includes(filterTag)
    );
    return (
      <div
        key={filterTag}
        className="text-xs text-primary opacity-100 font-medium"
      >
        <Tooltip className="opacity-100">
          <TooltipTrigger>
            <InfoIcon className="w-4 h-4" />
          </TooltipTrigger>
          <TooltipContent className="text-xs !bg-white !opacity-100 p-3 py-2 italic font-semibold ">
            {`This filter applies to`}
            <span className="font-medium px-1 grid grid-cols-2 gap-1 mt-1">
              {appliesToTopics
                ?.map((topic) => topic.Label)
                .map((label) => (
                  <span key={label}>{label}</span>
                ))}
            </span>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  if (filtersLoading) {
    return (
      <Card className="mb-4 bg-gradient-card shadow-modern border-modern-light animate-fade-in">
        <CardContent className="flex flex-col items-center justify-center py-2">
          <div className="flex flex-col items-center space-y-2">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600"></div>
              <div
                className="absolute inset-0 rounded-full h-16 w-16 border-4 border-transparent border-t-primary-400 animate-spin"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="text-center space-y-1">
              <p className="text-heading dark:text-white text-gray-900">{`Loading Filters`}</p>
              <p className="text-sm dark:text-white">
                {`Fetching available filters and topics...`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`mb-2 gap-2 bg-gradient-card shadow-modern border-modern-light animate-slide-up group transition-all duration-300 ${
        Object.keys(filterState).length > 0 ? "ring-2 ring-primary-100" : ""
      }`}
    >
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-primary-400 to-primary-300 rounded-lg">
              <FilterIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <CardTitle className="text-heading text-gray-900 dark:text-white">
                {`Custom Filters`}
              </CardTitle>
              <p className="text-caption dark:text-white">
                {`Refine your data view with advanced filtering options`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {Object.keys(filterState).some(
              (key) => filterState[key]?.length > 0
            ) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterState({})}
                color="red"
                type="cancel"
                withoutborder={true}
              >
                {`Clear All Filters`}
              </Button>
            )}
            {hasUnappliedChanges() && (
              <Button
                onClick={handleApplyFilters}
                disabled={loading}
                color="green"
                size="sm"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>{`Applying...`}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 !whitespace-nowrap">
                    <FilterIcon className="w-4 h-4" />
                    <span>{`Apply Filters`}</span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={`flex flex-col gap-2`}>
        {/* Filter Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 mb-4">
          <div className="flex flex-col items-start gap-3.5">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm font-semibold text-gray-900 flex items-center gap-2 dark:text-white">
                {`Cumulative From:`}
              </span>
              {cumulativeFromDate && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-none h-auto"
                  onClick={() => {
                    setCumulativeFromDate(null);
                    setDebouncedCumulativeDate(null);
                    setTempCumulativeFromDate(null);
                  }}
                >
                  {`Reset`}
                </Button>
              )}
            </div>
            <div className="w-full">
              <Input
                type="date"
                value={
                  tempCumulativeFromDate !== null
                    ? tempCumulativeFromDate
                    : cumulativeFromDate || ""
                }
                onChange={(e) => {
                  // Store temporary selection - don't apply yet
                  if (e.target.value && e.target.value.length === 10) {
                    setTempCumulativeFromDate(e.target.value);
                  } else {
                    setTempCumulativeFromDate(null);
                  }
                }}
                max={selectedBusinessDay}
                className="text-slate-900 font-semibold dark:text-white border-primary-200 focus:border-primary-400 focus:ring-primary-100 transition-all duration-200"
              />
              {/* Show Apply/Cancel buttons when a valid temporary date is selected */}
              {tempCumulativeFromDate !== null &&
                tempCumulativeFromDate.length === 10 &&
                tempCumulativeFromDate !== cumulativeFromDate && (
                  <div className="flex gap-2 justify-end mt-2">
                    <Button
                      variant="outline"
                      type="cancel"
                      color="red"
                      size="xs"
                      onClick={() => {
                        setTempCumulativeFromDate(null);
                      }}
                    >
                      {`Cancel`}
                    </Button>
                    <Button
                      color="green"
                      size="xs"
                      onClick={() => {
                        // Only apply if a date has been selected
                        if (
                          tempCumulativeFromDate &&
                          tempCumulativeFromDate.length === 10
                        ) {
                          setCumulativeFromDate(tempCumulativeFromDate);
                          setDebouncedCumulativeDate(tempCumulativeFromDate);
                          setTempCumulativeFromDate(null);
                        }
                      }}
                    >
                      {`Apply`}
                    </Button>
                  </div>
                )}
            </div>
          </div>
          {availableFilters.map((filter) => (
            <div key={filter.Tag} className="space-y-3 group">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-900 flex items-center gap-2 dark:text-white">
                  <span>{filter.Label}</span>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {resolveAppliesToTopic(filter.Tag)}
                  </div>
                </Label>
              </div>
              <div className="relative">
                <MultiSelect
                  options={
                    filter.Values?.map((value) => ({
                      value: value.Code,
                      label: value.Label,
                    })) || []
                  }
                  selectedValues={filterState[filter.Tag] || []}
                  onSelectionChange={(values) =>
                    handleFilterChange(filter.Tag, values)
                  }
                  placeholder={`Select ${filter.Label}`}
                  className="w-full dark:bg-gray-700 dark:text-white dark:border-gray-600"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Selected Filters Display */}
        {Object.keys(filterState).length > 0 && (
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <div className="w-1 h-4 bg-gradient-to-b from-primary-500 to-indigo-600 rounded-full"></div>
              <span className="text-xs text-primary-500 bg-primary-100 px-2 py-1 rounded-full dark:bg-primary-400 dark:text-white">
                {Object.keys(filterState).length}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {`Active Filters`}
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.keys(filterState).map((key, index) => {
                const filter = filtersData.Filters.find((f) => f.Tag === key);
                return (
                  <div key={key} className="group">
                    <BadgeComponent
                      key={index}
                      text={
                        <div className="flex items-center gap-1 single-selected-filter rounded-lg group text-sm">
                          <span className="text-xs font-semibold">
                            {filter?.Label}:
                          </span>
                          <span className={getMixedFontClass()}>
                            {(() => {
                              const selectedLabels = filterState[key].map(
                                (code) => {
                                  const value = filter?.Values?.find(
                                    (v) => v.Code === code
                                  );
                                  return value?.Label || code;
                                }
                              );

                              const totalValues = filter?.Values?.length || 0;
                              const isAllSelected =
                                selectedLabels.length === totalValues;

                              if (isAllSelected) {
                                return "All";
                              } else if (selectedLabels.length === 1) {
                                return selectedLabels[0];
                              } else {
                                return (
                                  <span className="text-primary-600 font-semibold">
                                    {selectedLabels.length} selected
                                  </span>
                                );
                              }
                            })()}
                          </span>
                          <X
                            className="size-5 max-w-max w-full text-gray-400 cursor-pointer group-hover:text-red-500 hover:scale-125 hover:animate-pulse transition-all duration-700 ease-in-out"
                            onClick={() => removeFilter(key)}
                          />
                        </div>
                      }
                      color="primary"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilterSection;
